/**
 * Utility functions for handling @mentions in posts and comments
 */

import { supabase } from "../integrations/supabase/client";
import { logger } from "../lib/logger";

export interface MentionedUser {
  id: string;
  username: string | null;
  full_name: string | null;
  avatar_url: string | null;
}

/**
 * Test function to verify mention extraction works correctly
 * This can be called from the console for debugging
 */
export const testMentionExtraction = (text: string) => {
  logger.info("🧪 Testing mention extraction for:", text);
  const mentions = extractMentions(text);
  logger.info("🧪 Test result:", mentions);
  return mentions;
};

/**
 * Extract @username mentions from text
 * Returns array of unique usernames (without the @ symbol)
 * Updated regex to handle usernames with @ symbols and special characters
 */
export const extractMentions = (text: string): string[] => {
  // Updated regex to handle usernames with @ symbols and special characters
  // This regex looks for @username patterns but allows @ symbols within the username
  // It stops at whitespace, punctuation (except @), or end of string
  const mentionRegex = /@([a-zA-Z0-9_@.-]+)(?=\s|[^\w@.-]|$)/g;
  const mentions: string[] = [];
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    const username = match[1].toLowerCase(); // Store usernames in lowercase for consistency
    mentions.push(username);
  }

  // Return unique mentions only
  return Array.from(new Set(mentions));
};

/**
 * Fetch user profiles for mentioned usernames
 * Returns array of user objects for valid usernames
 */
export const fetchMentionedUsers = async (
  usernames: string[]
): Promise<MentionedUser[]> => {
  if (usernames.length === 0) {
    return [];
  }

  try {
    // Query profiles table for users with matching usernames (case-insensitive)
    const { data: profiles, error } = await supabase
      .from("profiles")
      .select("id, username, full_name, avatar_url")
      .or(usernames.map((username) => `username.ilike.${username}`).join(","))
      .not("username", "is", undefined); // Ensure username is not undefined

    if (error) {
      logger.error("❌ fetchMentionedUsers: Database error:", error);
      return [];
    }

    // Filter out profiles with undefined usernames and convert to MentionedUser type
    const validMentionedUsers: MentionedUser[] = (profiles || [])
      .filter(
        (
          profile
        ): profile is {
          id: string;
          username: string;
          full_name: string | null;
          avatar_url: string | null;
        } => profile.username !== undefined && profile.username !== null
      )
      .map((profile) => ({
        id: profile.id,
        username: profile.username,
        full_name: profile.full_name,
        avatar_url: profile.avatar_url,
      }));

    return validMentionedUsers;
  } catch (error) {
    logger.error("❌ fetchMentionedUsers: Exception:", error);
    return [];
  }
};

/**
 * Process mentions in text and return both the mentions and user data
 */
export const processMentions = async (
  text: string
): Promise<{
  mentions: string[];
  mentionedUsers: MentionedUser[];
}> => {
  const mentions = extractMentions(text);
  const mentionedUsers = await fetchMentionedUsers(mentions);
  return { mentions, mentionedUsers };
};

/**
 * Replace @mentions in text with clickable links (for display purposes)
 */
export const formatMentionsForDisplay = (
  text: string,
  mentionedUsers: MentionedUser[]
): string => {
  const userMap = new Map(
    mentionedUsers.map((user) => [user.username?.toLowerCase(), user])
  );

  // Use the same regex as extractMentions for consistency
  return text.replace(
    /@([a-zA-Z0-9_@.-]+)(?=\s|[^\w@.-]|$)/g,
    (match, username) => {
      const user = userMap.get(username.toLowerCase());
      if (user) {
        // Return a formatted mention (could be enhanced with HTML/markdown later)
        return `[@${user.username}](/community/users/${user.username})`;
      }
      return match; // Return original if user not found
    }
  );
};
